/**
 * Recursive "did the user put anything here?" check for form values.
 *
 * Formly hands back deeply nested objects for composite fields (address, rdw, kvk, geo) and
 * arrays for repeaters and document uploads. A shallow falsy check would call
 * `{ street: '', city: '' }` filled, so emptiness has to be evaluated leaf by leaf.
 *
 * `0` and `false` count as VALUES by default — in a review summary an explicit "Nee" is something
 * the user confirmed and must be shown. `Date` is treated as a leaf so its internal properties are
 * never walked.
 *
 * Pass `treatFalseAsEmpty` when the question is "did the user enter anything here?" rather than
 * "is there anything to display?". A checkbox that has never been ticked reports `false` from the
 * moment the form initialises, so a step built out of toggles would otherwise look filled in before
 * the user had seen it. `0` stays a value either way: an unticked box has a default, a number does
 * not.
 */
export function isEmptyValue(value: unknown, treatFalseAsEmpty = false): boolean {
  if (value === null || value === undefined || value === '') {
    return true;
  }
  if (treatFalseAsEmpty && value === false) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.every(item => isEmptyValue(item, treatFalseAsEmpty));
  }
  if (typeof value === 'object' && !(value instanceof Date)) {
    return Object.values(value as Record<string, unknown>).every(inner => isEmptyValue(inner, treatFalseAsEmpty));
  }
  return false;
}
